
import Vue from 'vue'
import firebase from 'firebase/compat/app'
import 'firebase/compat/firestore'
import { showSnackbar } from '@/globalActions'

const SKIP_KEYS = new Set(['id'])
const SECRET_KEY_RE = /secret|password|token/i

type FieldType = 'boolean' | 'number' | 'string' | 'json'

export default Vue.extend({
  name: 'SpiideoSettingsPage',
  data: () => ({
    loading: true,
    saving: false,
    missingDoc: false,
    form: {} as Record<string, any>,
    fieldTypes: {} as Record<string, FieldType>,
    secretVisible: {} as Record<string, boolean>,
    jsonErrors: {} as Record<string, string[]>
  }),
  computed: {
    formKeys (): string[] {
      return Object.keys(this.form).filter((key) => !SKIP_KEYS.has(key))
    }
  },
  created (): void {
    this.loadSettings()
  },
  methods: {
    isSecretKey (key: string): boolean {
      return SECRET_KEY_RE.test(key)
    },
    fieldLabel (key: string): string {
      const i18nKey = `spiideo-setting-${key}`
      if (this.$te(i18nKey)) {
        return this.$t(i18nKey) as string
      }
      return key
    },
    toggleSecret (key: string): void {
      if (!this.isSecretKey(key)) return
      this.$set(this.secretVisible, key, !this.secretVisible[key])
    },
    detectType (value: unknown): FieldType {
      if (typeof value === 'boolean') return 'boolean'
      if (typeof value === 'number') return 'number'
      if (value !== null && typeof value === 'object') return 'json'
      return 'string'
    },
    async loadSettings (): Promise<void> {
      this.loading = true
      this.missingDoc = false
      try {
        const snap = await firebase.firestore()
          .collection('settings')
          .doc('spiideo')
          .get()

        if (!snap.exists) {
          this.missingDoc = true
          this.form = {}
          this.fieldTypes = {}
          return
        }

        const data = snap.data() || {}
        const form: Record<string, any> = {}
        const fieldTypes: Record<string, FieldType> = {}
        const secretVisible: Record<string, boolean> = {}

        Object.keys(data).forEach((key) => {
          if (SKIP_KEYS.has(key)) return
          const value = data[key]
          const type = this.detectType(value)
          fieldTypes[key] = type
          if (type === 'json') {
            form[key] = JSON.stringify(value, null, 2)
          } else {
            form[key] = value
          }
          if (this.isSecretKey(key)) {
            secretVisible[key] = false
          }
        })

        this.form = form
        this.fieldTypes = fieldTypes
        this.secretVisible = secretVisible
        this.jsonErrors = {}
      } catch (err) {
        console.error('loadSpiideoSettings', err)
        showSnackbar(
          (err && (err as Error).message) || (this.$t('spiideo-settings-load-failed') as string),
          { duration: 5000, color: 'danger' }
        )
      } finally {
        this.loading = false
      }
    },
    buildPayload (): Record<string, any> | null {
      const payload: Record<string, any> = {}
      const jsonErrors: Record<string, string[]> = {}
      let hasError = false

      this.formKeys.forEach((key) => {
        const type = this.fieldTypes[key]
        const value = this.form[key]

        if (type === 'json') {
          try {
            payload[key] = JSON.parse(value || 'null')
          } catch (e) {
            hasError = true
            jsonErrors[key] = [this.$t('spiideo-settings-invalid-json') as string]
          }
        } else if (type === 'number') {
          payload[key] = value === '' || value === null || value === undefined
            ? value
            : Number(value)
        } else {
          payload[key] = value
        }
      })

      this.jsonErrors = jsonErrors
      if (hasError) return null
      return payload
    },
    async save (): Promise<void> {
      const payload = this.buildPayload()
      if (!payload) {
        showSnackbar(this.$t('spiideo-settings-invalid-json') as string, {
          duration: 5000,
          color: 'danger'
        })
        return
      }

      this.saving = true
      try {
        await firebase.firestore()
          .collection('settings')
          .doc('spiideo')
          .update(payload)
        showSnackbar(this.$t('spiideo-settings-saved') as string, { color: 'success' })
      } catch (err) {
        console.error('saveSpiideoSettings', err)
        showSnackbar(
          (err && (err as Error).message) || (this.$t('spiideo-settings-save-failed') as string),
          { duration: 5000, color: 'danger' }
        )
      } finally {
        this.saving = false
      }
    }
  }
})
